import {
  Component, Inject, Input, OnChanges, OnDestroy, OnInit, Output, EventEmitter, PLATFORM_ID, SimpleChanges
} from '@angular/core';
import { PhotographerListItemComponent } from '../photographer-list-item/photographer-list-item.component';
import { Photographer } from '../../../entities/photographer/photographer.entity';
import { PhotographerService } from '../../../services/photographer/photographer.service';
import { TranslocoPipe } from '@jsverse/transloco';
import { isPlatformBrowser } from '@angular/common';
import {RouterLink} from '@angular/router';

@Component({
  selector: 'app-photographer-list',
  imports: [PhotographerListItemComponent, TranslocoPipe, RouterLink],
  templateUrl: './photographer-list.component.html',
  styleUrl: './photographer-list.component.scss'
})
export class PhotographerListComponent implements OnInit, OnChanges, OnDestroy {
  @Input() city: string | null = null;
  @Input() tags: string[] | null = null;

  /** Emit cumulative results impressions to parent (LandingComponent) */
  @Output() resultsImpression = new EventEmitter<{ resultsCount: number; topIds?: number[] }>();
  /** Emit click event on a result with its current rank */
  @Output() resultClick = new EventEmitter<{ photographerId: number; rank: number }>();
  /** Optional: empty state event if you want to track it upstream later */
  @Output() emptyStateView = new EventEmitter<void>();

  public photographers: Photographer[] = [];
  private page = 0;
  private limit = 20;
  public hasMore = true;
  public initialLoading = false;
  public loadingMore = false;

  constructor(
    private readonly photographerService: PhotographerService,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) {}

  ngOnInit(): void {
    this.initialLoad();
    if (isPlatformBrowser(this.platformId)) {
      window.addEventListener('scroll', this.onScroll, true);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['city'] && !changes['city'].firstChange) ||
      (changes['tags'] && !changes['tags'].firstChange)) {
      this.resetAndReload();
    }
  }

  ngOnDestroy(): void {
    if (isPlatformBrowser(this.platformId)) {
      window.removeEventListener('scroll', this.onScroll, true);
    }
  }

  /** Handle anchor clicks to emit analytics-friendly payload */
  public onCardClick(photographerId: number, rank: number): void {
    this.resultClick.emit({ photographerId, rank });
  }

  /** Infinite scroll hook */
  private onScroll = (): void => {
    const bottomReached = (window.innerHeight + window.scrollY) >= (document.body.offsetHeight - 200);
    if (bottomReached && this.hasMore && !this.loadingMore && !this.initialLoading) {
      this.loadMorePhotographers();
    }
  };

  /** Initial fetch with impression emission */
  private initialLoad(): void {
    this.initialLoading = true;
    this.photographerService.getPhotographers(this.city, this.tags, this.page, this.limit)
      .subscribe({
        next: res => {
          this.photographers = res.data;
          this.page++;
          this.hasMore = (this.page * this.limit) < res.total;
          this.initialLoading = false;

          // Emit impressions once we have the first batch
          if (this.photographers.length > 0) {
            this.emitImpression();
          } else {
            // Track empty state if nothing found
            this.emptyStateView.emit();
          }
        },
        error: () => this.initialLoading = false
      });
  }

  /** Pagination fetch with impression emission (cumulative) */
  private loadMorePhotographers(): void {
    this.loadingMore = true;
    this.photographerService.getPhotographers(this.city, this.tags, this.page, this.limit)
      .subscribe({
        next: res => {
          this.photographers = [...this.photographers, ...res.data];
          this.page++;
          this.hasMore = (this.page * this.limit) < res.total;
          this.loadingMore = false;

          if (res.data.length > 0) {
            this.emitImpression();
          }
        },
        error: () => this.loadingMore = false
      });
  }

  /** Reset on filters change and reload the first page */
  private resetAndReload(): void {
    this.page = 0;
    this.photographers = [];
    this.hasMore = true;
    this.initialLoad();
  }

  /** Emit cumulative impression with top 5 IDs (useful for ranking analysis) */
  private emitImpression(): void {
    const topIds = this.photographers.slice(0, 5).map(p => (p as any).id).filter(Boolean);
    this.resultsImpression.emit({
      resultsCount: this.photographers.length,
      topIds,
    });
  }
}
